import { gsap } from "gsap";
import { Container } from "pixi.js";

import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { playSound, playSoundOnce } from "../../../abstracts/utils/SoundUtils";
import { UCSoundNames, UCSpineNames } from "../../assetsLoader/UCAssetsNames";
import { UCWildSymbol } from "./symbols/UCWildSymbol";

export class UCFreespinMultiplier extends Container {
    protected mainSpine: AbstractSpine;
    protected particlesSpine: AbstractSpine;
    protected multiplierContainersArr: Container[] = [];

    public currentCounter: number = 0;

    constructor() {
        super();

        this.init();
    }

    private init() {
        this.mainSpine = new AbstractSpine(UCSpineNames.FS_BOAT_MULTIPLIER);
        this.mainSpine.name = `fsMultiplierSpine`;

        this.addChild(this.mainSpine);

        for (let i = 0; i < 3; i++) {
            let textureName = `X2`;
            let multiplierPosition: gsap.Point2D = { x: -112, y: -42 };

            if (i === 1) {
                multiplierPosition = { x: 27, y: -35 };
                textureName = `X3`;
            } else if (i === 2) {
                multiplierPosition = { x: 164, y: -38 };
                textureName = `X10`;
            }

            const multiplierContainer = new Container();
            multiplierContainer.name = textureName;
            multiplierContainer.position.set(multiplierPosition.x, multiplierPosition.y);

            this.multiplierContainersArr.push(multiplierContainer);
            this.addChild(multiplierContainer);
        }

        this.particlesSpine = new AbstractSpine(UCSpineNames.BOAT_MULTIPLIER_PARTICLES);
    }

    public getWildToBoatAnimation(
        progression: number,
        isRestore: boolean = false,
        wildSymbol: UCWildSymbol | undefined = undefined
    ): gsap.core.Tween | gsap.core.Timeline {
        if (isRestore) {
            return gsap.delayedCall(0.01, () => {
                this.mainSpine.setSkinByName(`FS_boat`);
                if (progression > 0) {
                    this.currentCounter = progression - 1;

                    this.currentCounter++;

                    if (this.currentCounter > 12) {
                        this.currentCounter = 12;
                    }

                    this.mainSpine.state.setAnimation(0, `x${this.currentCounter}`, false);
                }
            });
        } else {
            if (wildSymbol !== undefined) {
                this.mainSpine.setSkinByName(`FS_boat`);

                const wildPosition = this.toLocal(wildSymbol.getSpineGlobalPosition());

                const defaultDistance = 500;
                const dist = Math.sqrt(
                    (wildPosition.x - this.x) ** 2 + (wildPosition.y - this.y) ** 2
                );
                let animationDuration = 0.5 + (0.6 * dist) / defaultDistance;
                let currentMultiplierNumber = Math.floor(this.currentCounter / 4);

                if (progression > 0) {
                    this.currentCounter++;

                    if (this.currentCounter > 12) {
                        this.currentCounter = 12;

                        return gsap.to({}, {});
                    }
                }

                const lastCounter = this.currentCounter;

                const fishSpine = new AbstractSpine(UCSpineNames.FISH_TO_BOAT_MULTIPLIER);
                fishSpine.alpha = 0;
                fishSpine.position.set(wildPosition.x, wildPosition.y + 12);

                const multiplierPosition = this.toLocal(
                    this.multiplierContainersArr[currentMultiplierNumber].getGlobalPosition()
                );

                const timeline = gsap.timeline({
                    smoothChildTiming: true,
                    onComplete: () => {
                        this.removeChild(fishSpine);
                        fishSpine.destroy();
                    },
                });

                const showFishTween = gsap.to(fishSpine, {
                    duration: 0.4,
                    alpha: 1,
                    onStart: () => {
                        fishSpine.state.setAnimation(0, `glow`, true);
                        this.addChild(fishSpine);
                    },
                });

                timeline.add(showFishTween, 0);

                const moveWildToMultiplierTween = gsap.to(fishSpine, {
                    duration: animationDuration,
                    x: multiplierPosition.x,
                    y: multiplierPosition.y,
                    pixi: { scale: 0.4 },
                    ease: `sine.out`,
                    onStart: () => {
                        playSound({ name: UCSoundNames.WILD_TO_BOAT });
                    },
                });

                timeline.add(moveWildToMultiplierTween);

                const hideWildGraphicsClone = gsap.to(fishSpine, {
                    duration: animationDuration * 0.4,
                    alpha: 0,
                    pixi: { scale: 0 },
                });
                timeline.add(hideWildGraphicsClone, `>-=-${animationDuration * 0.1}`);

                timeline.call(
                    () => {
                        if (lastCounter === 4) {
                            playSoundOnce({
                                name: UCSoundNames.BOAT_MULTIPLIER_X2,
                                playOnceMinDuration: 2,
                            });
                        } else if (lastCounter === 8) {
                            playSoundOnce({
                                name: UCSoundNames.BOAT_MULTIPLIER_X3,
                                playOnceMinDuration: 2,
                            });
                        } else if (lastCounter === 12) {
                            playSoundOnce({
                                name: UCSoundNames.BOAT_MULTIPLIER_X10,
                                playOnceMinDuration: 2,
                            });
                        } else {
                            if (lastCounter === 1 || lastCounter === 5 || lastCounter === 9) {
                                playSound({
                                    name: UCSoundNames.BOAT_FISH_1,
                                });
                            } else if (
                                lastCounter === 2 ||
                                lastCounter === 6 ||
                                lastCounter === 10
                            ) {
                                playSound({
                                    name: UCSoundNames.BOAT_FISH_2,
                                });
                            } else if (
                                lastCounter === 3 ||
                                lastCounter === 7 ||
                                lastCounter === 11
                            ) {
                                playSound({
                                    name: UCSoundNames.BOAT_FISH_3,
                                });
                            }
                        }
                        this.mainSpine.state.setAnimation(0, `x${lastCounter}`, false);

                        this.particlesSpine.x = multiplierPosition.x + 116;
                        if (lastCounter > 8) {
                            this.particlesSpine.x += 6;
                        }

                        this.particlesSpine.y = 8;

                        this.particlesSpine.getSpineTween(`bubbles2`, true, null, null, () => {
                            this.removeChild(this.particlesSpine);
                        });
                        this.addChild(this.particlesSpine);
                    },
                    [],
                    `<+=${animationDuration * 0.18}`
                );

                return timeline;
            }
        }

        return gsap.to({}, {});
    }

    public getBoatToWildMultiplyAnimation(
        wildSymbol: UCWildSymbol,
        multiplier: number
    ): gsap.core.Tween | gsap.core.Timeline {
        const currentMultiplierNumber = Math.floor(this.currentCounter / 4) - 1;

        if (currentMultiplierNumber < 0) {
            return gsap.to({}, {});
        }

        const currentMultiplierContainer = this.multiplierContainersArr[currentMultiplierNumber];

        const multiplierNumberSpine = new AbstractSpine(UCSpineNames.BOAT_TO_WILD_MULTIPLIER);

        multiplierNumberSpine.position.set(
            currentMultiplierContainer.x,
            currentMultiplierContainer.y
        );

        const multiplierToWildTimeline = gsap.timeline({
            onComplete: () => {
                this.removeChild(multiplierNumberSpine);
                multiplierNumberSpine.destroy();
            },
        });

        const wildPosition = this.toLocal(wildSymbol.getSpineGlobalPosition());
        const defaultDistance = 500;
        const dist = Math.sqrt((wildPosition.x - this.x) ** 2 + (wildPosition.y - this.y) ** 2);
        let animationDuration = 0.5 + (0.6 * dist) / defaultDistance;

        const moveMultiplierToWildTween = gsap.to(multiplierNumberSpine, {
            duration: animationDuration,
            x: wildPosition.x,
            y: wildPosition.y + 15,
            ease: `sine.out`,
            onStart: () => {
                multiplierNumberSpine.setSkinByName(currentMultiplierContainer.name.toLowerCase());
                multiplierNumberSpine.state.setAnimation(0, `glow`, true);
                this.addChild(multiplierNumberSpine);

                playSound({ name: UCSoundNames.BOAT_TO_WILD });
            },
        });

        multiplierToWildTimeline.add(moveMultiplierToWildTween, 0);

        const hideWildGraphicsClone = gsap.to(multiplierNumberSpine, {
            duration: 0.2,
            alpha: 0,
            pixi: { scale: 0.1 },
            ease: `power1.in`,
        });
        multiplierToWildTimeline.add(hideWildGraphicsClone);

        const updateWildMultiplier = wildSymbol.updateMultiplierFromBoat(multiplier);
        multiplierToWildTimeline.add(updateWildMultiplier);

        return multiplierToWildTimeline;
    }

    public changeToFSSkin(): void {
        this.mainSpine.setSkinByName(`FS_boat`);
    }

    public enableIdle() {
        this.mainSpine.getSpineTween(`x0`, true);
    }

    public reset() {
        this.mainSpine.reset();
        this.mainSpine.setSkinByName(`Base_boat`);
        this.enableIdle();

        this.currentCounter = 0;
    }
}
